/**
 * Admin Marketing — publishing provider registry.
 *
 * We buy the publishing rail rather than build five OAuth integrations, because
 * the cost of building is not the code — it is Meta App Review, LinkedIn
 * Community Management API approval, and a paid X tier, none of which we
 * control. An aggregator that publishes under its own app registrations removes
 * that from the critical path entirely.
 *
 * All providers sit behind one contract in ff-api-external (`/api/social/*`),
 * so switching is a client swap rather than a rewrite. That abstraction is also
 * what makes betting on a young, cheap vendor reasonable instead of reckless.
 *
 * IMPORTANT — this UI must stay capability-driven, never provider-aware. No
 * component should branch on `provider.id === 'zernio'`. Read the matrix and
 * drive affordances off it, so adding a sixth provider costs zero React
 * changes. In production this shape is served by
 * `GET /api/admin/v2/marketing/providers`; the constant below is the seed.
 */

export type ProviderId = 'zernio' | 'socialapi' | 'blotato' | 'uploadpost' | 'ayrshare';

export type ProviderCapability =
  | 'publish'
  | 'schedule'
  | 'thread'
  | 'first_comment'
  | 'media_image'
  | 'media_video'
  | 'read_posts'
  | 'read_comments'
  | 'reply_comment'
  | 'read_dms'
  | 'read_reviews'
  | 'post_insights'
  | 'webhooks'
  | 'dry_run_validate'
  | 'oauth_connect';

/**
 * `unknown` is deliberately distinct from `none`.
 *
 * Several vendors simply do not document a capability — Zernio never states
 * whether it supports X threads, nor whose platform app registrations it uses.
 * Rendering "not supported" for those would be the same class of error as
 * reporting a handle as dead when the check itself failed: presenting absence
 * of evidence as evidence of absence. The conformance run resolves `unknown`
 * into a real answer; until then the UI must say so.
 */
export type CapabilitySupport = 'full' | 'partial' | 'none' | 'unknown';

export interface CapabilityFact {
  support: CapabilitySupport;
  /** Shown in the UI wherever a control is disabled, so the reason is never mysterious. */
  note?: string;
}

export interface ProviderRecord {
  id: ProviderId;
  name: string;
  baseUrl: string;
  authHeader: string;
  /** Rough monthly cost at our scale (~6 connected accounts). Verify before committing. */
  costNote: string;
  platforms: string[];
  capabilities: Record<ProviderCapability, CapabilityFact>;
  /** Whether the vendor publishes under its own platform app registrations. */
  appRegistrations: CapabilitySupport;
  appRegistrationsNote: string;
  docsUrl: string;
  enabled: boolean;
  isDefault: boolean;
}

export const PROVIDER_CAPABILITY_LABEL: Record<ProviderCapability, string> = {
  publish: 'Publish',
  schedule: 'Schedule',
  thread: 'Threads',
  first_comment: 'First comment',
  media_image: 'Images',
  media_video: 'Video',
  read_posts: 'Read posts',
  read_comments: 'Read comments',
  reply_comment: 'Reply to comment',
  read_dms: 'Read DMs',
  read_reviews: 'Reviews',
  post_insights: 'Post insights',
  webhooks: 'Webhooks',
  dry_run_validate: 'Dry-run validate',
  oauth_connect: 'OAuth connect',
};

export const CAPABILITY_SUPPORT_BADGE: Record<CapabilitySupport, string> = {
  full: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  none: 'bg-rose-50 text-rose-700 border-rose-200',
  unknown: 'bg-gray-100 text-gray-600 border-gray-200',
};

export const CAPABILITY_SUPPORT_LABEL: Record<CapabilitySupport, string> = {
  full: 'Yes',
  partial: 'Partial',
  none: 'No',
  unknown: 'Undocumented',
};

/**
 * Capabilities that Radar depends on. A provider weak here quietly disables
 * half the module — the Replies inbox and the Performance charts — which is why
 * price is the wrong axis to choose on.
 */
export const READ_BACK_CAPABILITIES: ProviderCapability[] = [
  'read_posts',
  'read_comments',
  'reply_comment',
  'post_insights',
  'webhooks',
];

const F = (note?: string): CapabilityFact => ({ support: 'full', note });
const P = (note: string): CapabilityFact => ({ support: 'partial', note });
const N = (note: string): CapabilityFact => ({ support: 'none', note });
const U = (note: string): CapabilityFact => ({ support: 'unknown', note });

/** Sourced from each vendor's own API documentation, August 2026. */
export const PROVIDERS: ProviderRecord[] = [
  {
    id: 'zernio',
    name: 'Zernio',
    baseUrl: 'https://zernio.com/api/v1',
    authHeader: 'Authorization: Bearer sk_…',
    costNote: '~$36/mo at 6 accounts (tiered $6/$3/$1 per account). Free tier: 2 accounts, unlimited posts, full API.',
    platforms: ['x', 'instagram', 'facebook', 'linkedin', 'tiktok', 'youtube', 'threads', 'pinterest', 'reddit', 'bluesky', 'google_business', 'telegram', 'snapchat', 'whatsapp', 'discord'],
    capabilities: {
      publish: F('POST /posts'),
      schedule: F('scheduledFor + timezone'),
      thread: U('Not documented — resolve via conformance run'),
      first_comment: F('Listed as supported'),
      media_image: F(),
      media_video: F(),
      read_posts: F('GET /posts/{postId}'),
      read_comments: F('GET /inbox/comments/{postId} — 8 platforms'),
      reply_comment: F('POST /inbox/comments/{postId}'),
      read_dms: F('GET /messages/list-inbox-conversations'),
      read_reviews: N('No reviews endpoint'),
      post_insights: F(
        'GET /v1/analytics + /daily-metrics + /best-time + /content-decay — connected accounts only. source=late (posted via API) vs source=external (posted natively on a connected profile). Cannot read competitor/listen-target feeds.',
      ),
      webhooks: F('New-comment events'),
      dry_run_validate: N('No validate endpoint'),
      oauth_connect: F('GET /connect/{platform}?profileId='),
    },
    appRegistrations: 'unknown',
    appRegistrationsNote:
      'Docs never state whose platform apps are used. This is the single question that decides whether buying removes the approvals bottleneck — confirm per platform before go-live.',
    docsUrl: 'https://docs.zernio.com/',
    enabled: true,
    isDefault: true,
  },
  {
    id: 'socialapi',
    name: 'SocialAPI.ai',
    baseUrl: 'https://api.social-api.ai/v1',
    authHeader: 'Authorization: Bearer sapi_key_…',
    costNote: '~$29/mo (Side Hustle, up to 10 brands).',
    platforms: ['x', 'instagram', 'facebook', 'linkedin', 'tiktok', 'youtube', 'threads', 'whatsapp'],
    capabilities: {
      publish: F('POST /posts'),
      schedule: F('scheduled_at'),
      thread: U('Not documented'),
      first_comment: F('first_comment field'),
      media_image: F(),
      media_video: F(),
      read_posts: F('GET /posts/{id}'),
      read_comments: F('Per-comment can_reply / can_hide / can_like flags'),
      reply_comment: F('Incl. private replies'),
      read_dms: F('GET /inbox/conversations'),
      read_reviews: F('GET /inbox/reviews'),
      post_insights: F('GET /posts/{id}/metrics'),
      webhooks: F('Full suite — event types, deliveries, retry, test'),
      dry_run_validate: F('POST /posts/validate'),
      oauth_connect: F('POST /accounts/connect'),
    },
    appRegistrations: 'unknown',
    appRegistrationsNote: 'Not stated in public docs — confirm per platform.',
    docsUrl: 'https://docs.social-api.ai/',
    enabled: true,
    isDefault: false,
  },
  {
    id: 'blotato',
    name: 'Blotato',
    baseUrl: 'https://backend.blotato.com/v2',
    authHeader: 'blotato-api-key',
    costNote: 'Flat ~$29/mo, hard cap of 20 connected accounts.',
    platforms: ['x', 'instagram', 'facebook', 'linkedin', 'tiktok', 'youtube', 'threads', 'pinterest', 'bluesky'],
    capabilities: {
      publish: F('POST /posts'),
      schedule: F('scheduledTime (root-level, sibling of post)'),
      thread: F('additionalPosts[]'),
      first_comment: N('Absent from the request schema'),
      media_image: F(),
      media_video: F(),
      read_posts: F('GET /posts/{postSubmissionId}'),
      read_comments: P('Instagram and Facebook only'),
      reply_comment: P('Instagram and Facebook only'),
      read_dms: P('Instagram and Facebook only'),
      read_reviews: N('No reviews endpoint'),
      post_insights: P('LinkedIn returns no metrics'),
      webhooks: N('None documented — Replies inbox would have to poll'),
      dry_run_validate: N('No validate endpoint'),
      oauth_connect: N('Accounts connected via their dashboard, not the API'),
    },
    appRegistrations: 'unknown',
    appRegistrationsNote: 'Not stated. Account connection is dashboard-driven, which limits white-label use.',
    docsUrl: 'https://help.blotato.com/api',
    enabled: true,
    isDefault: false,
  },
  {
    id: 'uploadpost',
    name: 'Upload-Post',
    baseUrl: 'https://api.upload-post.com',
    authHeader: 'Authorization: Apikey …',
    costNote: '~$24/mo, unlimited posts on every paid plan.',
    platforms: ['x', 'instagram', 'facebook', 'linkedin', 'tiktok', 'youtube', 'threads', 'pinterest', 'reddit'],
    capabilities: {
      publish: F('upload_text / upload / upload_photos'),
      schedule: F('schedule_date'),
      thread: U('Not documented'),
      first_comment: N('No first-comment field'),
      media_image: F(),
      media_video: F('Video-first heritage; includes FFmpeg processing'),
      read_posts: F('GET /post-analytics/{request_id}'),
      read_comments: P('Instagram, Facebook, YouTube, LinkedIn — no TikTok'),
      reply_comment: F('POST /comments/create'),
      read_dms: N('AutoDM monitors only, no DM read API'),
      read_reviews: N('No reviews endpoint'),
      post_insights: F('Live + cached; live is rate-limited to 100 req / 5 min'),
      webhooks: N('None documented — Replies inbox would have to poll'),
      dry_run_validate: N('No validate endpoint'),
      oauth_connect: F('POST /oauth/{platform}/start'),
    },
    appRegistrations: 'unknown',
    appRegistrationsNote: 'Not stated — confirm per platform, TikTok especially.',
    docsUrl: 'https://docs.upload-post.com/',
    enabled: true,
    isDefault: false,
  },
  {
    id: 'ayrshare',
    name: 'Ayrshare',
    baseUrl: 'https://api.ayrshare.com',
    authHeader: 'Authorization: Bearer … + profile key',
    costNote: '~$149/mo single profile. The mature fallback — paying for track record, not features.',
    platforms: ['x', 'instagram', 'facebook', 'linkedin', 'tiktok', 'youtube', 'threads', 'pinterest', 'reddit', 'bluesky', 'telegram'],
    capabilities: {
      publish: F('POST /post'),
      schedule: F('scheduleDate'),
      thread: F('twitterOptions.thread'),
      first_comment: F('instagramOptions.firstComment'),
      media_image: F(),
      media_video: F(),
      read_posts: F('GET /history'),
      read_comments: F('GET /comments'),
      reply_comment: F('POST /comments/reply'),
      read_dms: U('Not documented'),
      read_reviews: U('Not documented'),
      post_insights: F('GET /analytics/post + /analytics/social'),
      webhooks: F('POST/GET/DELETE /webhooks'),
      dry_run_validate: N('No validate endpoint'),
      oauth_connect: F('POST /profiles/jwt — SSO linking'),
    },
    appRegistrations: 'full',
    appRegistrationsNote: 'Publishes under its own platform app registrations for most networks.',
    docsUrl: 'https://www.ayrshare.com/docs',
    enabled: false,
    isDefault: false,
  },
];

export function getProvider(id: ProviderId): ProviderRecord | undefined {
  return PROVIDERS.find((p) => p.id === id);
}

export function defaultProvider(): ProviderRecord {
  return PROVIDERS.find((p) => p.isDefault && p.enabled) ?? PROVIDERS[0];
}

/**
 * Whether a provider can do `capability`. `unknown` counts as unavailable for
 * gating purposes — we must not offer a control we cannot honour — but callers
 * should surface `note` so the user sees "undocumented", not "unsupported".
 */
export function supports(provider: ProviderRecord, capability: ProviderCapability): boolean {
  const support = provider.capabilities[capability]?.support;
  return support === 'full' || support === 'partial';
}

/** Count of read-back capabilities a provider fully supports — the ranking that matters. */
export function readBackScore(provider: ProviderRecord): number {
  return READ_BACK_CAPABILITIES.filter((c) => provider.capabilities[c]?.support === 'full').length;
}
